/**
 * useUsersData Hook - Manages user data fetching, filtering, and CRUD operations
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import apiClient from '../services/apiClient';
import { storage } from '../adapters/storage';
// Note: apiClient already has baseURL '/api', so we don't need API_BASE_URL here
import { msg, MESSAGES } from '../utils/messages';
import { resetUserPassword } from '../services/passwordService';
import { useConfirm } from './useConfirm';
import clienteService, { type Cliente } from '../services/clienteService';
import { type SMTPConfig } from '../components/SMTPConfigSection';
import { MultiSortConfig } from '../types/auditLog';
import {
  User,
  UserFilters,
  NewUserData,
  getInitialNewUser,
} from '../types/user';

interface UseUsersDataReturn {
  // Data
  users: User[];
  clientes: Cliente[];
  loading: boolean;
  error: string | null;

  // Pagination
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;

  // Sorting
  sortConfig: MultiSortConfig;
  handleSort: (key: string, event: React.MouseEvent) => void;

  // Filters
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedRoles: string[];
  setSelectedRoles: (roles: string[]) => void;
  selectedStatus: string[];
  setSelectedStatus: (status: string[]) => void;
  selectedClientes: number[];
  setSelectedClientes: (clientes: number[]) => void;
  filterCount: number;
  clearFilters: () => void;

  // CRUD Operations
  handleAtualizar: () => Promise<void>;
  handleCreateUser: (newUser: NewUserData) => Promise<boolean>;
  handleExcluir: (user: User) => Promise<void>;
  handleSaveEditedUser: (user: User, smtpConfig: SMTPConfig) => Promise<boolean>;
  handleResetPassword: (userId: number, newPassword: string) => Promise<{ success: boolean; password?: string; error?: string }>;
  handleExportar: () => Promise<void>;

  // Confirm dialog
  confirm: ReturnType<typeof useConfirm>['confirm'];
  ConfirmDialog: ReturnType<typeof useConfirm>['ConfirmDialog'];
}

export function useUsersData(): UseUsersDataReturn {
  // Confirm dialog
  const { confirm, ConfirmDialog } = useConfirm();

  // Token state
  const [token, setToken] = useState<string | null>(null);

  // Data states
  const [users, setUsers] = useState<User[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);

  // Sorting state
  // Story 2.51: ordenação default por first_name ASC (alfabético) — combobox/lista
  // de usuários começa ordenado. Usuário ainda pode trocar via header sortable.
  const [sortConfig, setSortConfig] = useState<MultiSortConfig>([
    { key: 'first_name', direction: 'asc' },
  ]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedClientes, setSelectedClientes] = useState<number[]>([]);

  // Build filters object
  const userFilters: UserFilters = useMemo(() => {
    const f: UserFilters = {};
    if (searchTerm.trim()) f.search = searchTerm.trim();
    if (selectedRoles.length > 0) f.roles = selectedRoles;
    if (selectedStatus.length > 0) f.status = selectedStatus;
    if (selectedClientes.length > 0) f.cliente_ids = selectedClientes;
    return f;
  }, [searchTerm, selectedRoles, selectedStatus, selectedClientes]);

  // Filter count
  const filterCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedRoles.length > 0) count++;
    if (selectedStatus.length > 0) count++;
    if (selectedClientes.length > 0) count++;
    return count;
  }, [searchTerm, selectedRoles, selectedStatus, selectedClientes]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedRoles([]);
    setSelectedStatus([]);
    setSelectedClientes([]);
  }, []);

  // Load token
  useEffect(() => {
    const loadToken = async () => {
      const accessToken = await storage.getItem('accessToken');
      setToken(accessToken);
    };
    loadToken();
  }, []);

  // Handle sort
  const handleSort = useCallback((key: string, event: React.MouseEvent) => {
    if (event.shiftKey) {
      // Multi-column sort
      const existingIndex = sortConfig.findIndex((s) => s.key === key);

      if (existingIndex >= 0) {
        const existing = sortConfig[existingIndex];
        const newConfig = [...sortConfig];

        if (existing.direction === 'asc') {
          newConfig[existingIndex] = { key, direction: 'desc' };
        } else {
          newConfig.splice(existingIndex, 1);
        }

        setSortConfig(newConfig);
      } else {
        if (sortConfig.length < 3) {
          setSortConfig([...sortConfig, { key, direction: 'asc' }]);
        } else {
          setSortConfig([...sortConfig.slice(0, 2), { key, direction: 'asc' }]);
        }
      }
    } else {
      // Single sort
      const existing = sortConfig.find((s) => s.key === key);
      const newDirection = existing?.direction === 'asc' ? 'desc' : 'asc';
      setSortConfig([{ key, direction: newDirection }]);
    }
    setCurrentPage(1);
  }, [sortConfig]);

  // Fetch users
  const handleAtualizar = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const params: Record<string, unknown> = {
        page: currentPage,
        page_size: pageSize,
      };

      if (userFilters.search) params.search = userFilters.search;
      if (userFilters.roles && userFilters.roles.length > 0) {
        params.roles = userFilters.roles.join(',');
      }
      if (userFilters.status && userFilters.status.length > 0) {
        params.is_active = userFilters.status.includes('ativo')
          ? true
          : (userFilters.status.includes('inativo') ? false : undefined);
      }
      if (userFilters.cliente_ids && userFilters.cliente_ids.length > 0) {
        params.cliente_ids = userFilters.cliente_ids.join(',');
      }
      if (sortConfig.length > 0) {
        params.ordering = sortConfig.map(s => `${s.direction === 'desc' ? '-' : ''}${s.key}`).join(',');
      }

      const response = await apiClient.get('/users', {
        params,
      });

      const data = response.data;
      if (Array.isArray(data)) {
        setUsers(data);
        setTotalPages(1);
        setTotalCount(data.length);
      } else {
        setUsers(data.results || []);
        setTotalPages(data.total_pages || 1);
        setTotalCount(data.count || 0);
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      console.error('Erro ao atualizar lista:', err);
      setError(axiosError.response?.data?.message || 'Erro ao carregar usuários');
      msg.error(MESSAGES.USER.REFRESH_ERROR);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, pageSize, userFilters, sortConfig]);

  // Fetch users when dependencies change
  useEffect(() => {
    if (token) {
      handleAtualizar();
    }
  }, [token, userFilters, sortConfig, currentPage, handleAtualizar]);

  // Fetch clientes for filter options
  useEffect(() => {
    const fetchClientes = async () => {
      if (!token) return;
      try {
        const data = await clienteService.listClientes();
        setClientes(data.filter(c => c.ativo));
      } catch (err) {
        console.error('Erro ao buscar clientes:', err);
      }
    };
    fetchClientes();
  }, [token]);

  // Create user
  const handleCreateUser = useCallback(async (newUser: NewUserData): Promise<boolean> => {
    if (!token) return false;

    // Validation
    if (!newUser.username || !newUser.full_name || !newUser.email || !newUser.password) {
      msg.warning(MESSAGES.GENERIC.REQUIRED_FIELDS);
      return false;
    }

    if (!newUser.confirmPassword) {
      msg.error('Por favor, confirme a senha');
      return false;
    }

    if (newUser.password !== newUser.confirmPassword) {
      msg.error('As senhas não coincidem');
      return false;
    }

    if (newUser.password.length < 8) {
      msg.error('A senha deve ter no mínimo 8 caracteres');
      return false;
    }

    try {
      const { confirmPassword, smtp_senha, ...restData } = newUser;
      const dataToSend = {
        ...restData,
        ...(smtp_senha && { smtp_senha }),
      };

      await apiClient.post('/users', dataToSend);

      msg.success(MESSAGES.USER.CREATED);
      handleAtualizar();
      return true;
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string | Record<string, any> | Array<any> } } };
      console.error('Erro ao criar usuário:', err);

      let errorMessage = 'Erro desconhecido';
      const detail = axiosError.response?.data?.detail;

      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic list of errors
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (typeof detail === 'object' && detail !== null) {
        // Django dict of errors
        errorMessage = Object.values(detail).flat().join(', ');
      }

      msg.error(MESSAGES.USER.CREATE_ERROR(errorMessage));
      return false;
    }
  }, [token, handleAtualizar]);

  // Delete user
  const handleExcluir = useCallback(async (userToDelete: User) => {
    if (!userToDelete || !token) return;

    const confirmed = await confirm({
      title: 'Excluir Usuário',
      message: (
        <>
          <p>{MESSAGES.USER.CONFIRM_DELETE(userToDelete.username)}</p>
          <p className="text-sm text-gray-500 mt-2">{MESSAGES.USER.CONFIRM_DELETE_MSG}</p>
        </>
      ),
      variant: 'danger',
      confirmText: 'Excluir',
    });

    if (!confirmed) return;

    try {
      setLoading(true);
      await apiClient.delete(`/users/${userToDelete.id}`);

      msg.success(MESSAGES.USER.DELETED);
      handleAtualizar();
    } catch (err) {
      console.error('Erro ao excluir usuário:', err);
      msg.error(MESSAGES.USER.DELETE_ERROR);
    } finally {
      setLoading(false);
    }
  }, [token, confirm, handleAtualizar]);

  // Save edited user
  const handleSaveEditedUser = useCallback(async (user: User, smtpConfig: SMTPConfig): Promise<boolean> => {
    if (!user || !token) return false;

    try {
      // Validate password if provided
      if (user.newPassword) {
        if (user.newPassword !== user.confirmPassword) {
          msg.error('As senhas não coincidem');
          return false;
        }
        if (user.newPassword.length < 8) {
          msg.error('A senha deve ter no mínimo 8 caracteres');
          return false;
        }
      }

      // Remove password fields from PUT payload — backend ignores them
      const { confirmPassword, newPassword, ...restData } = user;

      const dataToSend = {
        ...restData,
        ...smtpConfig,
      };

      await apiClient.put(`/users/${user.id}`, dataToSend);

      // Reset password via dedicated endpoint if provided
      if (user.newPassword) {
        try {
          await resetUserPassword(user.id, user.newPassword);
        } catch (err: any) {
          msg.error(`Dados salvos, mas erro ao alterar senha: ${err.message}`);
          handleAtualizar();
          return false;
        }
      }

      msg.success(MESSAGES.USER.UPDATED);
      handleAtualizar();
      return true;
    } catch (err) {
      console.error('Erro ao atualizar usuário:', err);
      msg.error(MESSAGES.USER.SAVE_ERROR);
      return false;
    }
  }, [token, handleAtualizar]);

  // Reset password
  const handleResetPassword = useCallback(async (
    userId: number,
    newPassword: string
  ): Promise<{ success: boolean; password?: string; error?: string }> => {
    if (!token) return { success: false, error: 'Token não disponível' };

    try {
      const response = await resetUserPassword(userId, newPassword);
      return { success: true, password: response.new_password || newPassword };
    } catch (err: any) {
      return {
        success: false,
        error: err.message || 'Erro ao resetar senha'
      };
    }
  }, [token]);

  // Export users
  const handleExportar = useCallback(async () => {
    if (totalCount === 0) {
      msg.warning(MESSAGES.USER.EXPORT_EMPTY);
      return;
    }

    try {
      setLoading(true);
      const params: Record<string, unknown> = {
        search: userFilters.search,
        roles: userFilters.roles?.join(','),
        is_active: userFilters.status?.includes('ativo')
          ? true
          : (userFilters.status?.includes('inativo') ? false : undefined),
        cliente_ids: userFilters.cliente_ids?.join(','),
        page_size: totalCount > 0 ? totalCount : 10000,
        ordering: sortConfig.map(s => `${s.direction === 'desc' ? '-' : ''}${s.key}`).join(','),
      };

      const response = await apiClient.get('/users/export', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `usuarios_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      msg.success(MESSAGES.USER.EXPORT_SUCCESS);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      console.error('Erro ao exportar usuários:', err);
      msg.error(`${MESSAGES.USER.EXPORT_ERROR}: ${axiosError.response?.data?.detail || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  }, [token, totalCount, userFilters, sortConfig]);

  return {
    // Data
    users,
    clientes,
    loading,
    error,

    // Pagination
    currentPage,
    totalPages,
    totalCount,
    pageSize,
    setCurrentPage,

    // Sorting
    sortConfig,
    handleSort,

    // Filters
    searchTerm,
    setSearchTerm,
    selectedRoles,
    setSelectedRoles,
    selectedStatus,
    setSelectedStatus,
    selectedClientes,
    setSelectedClientes,
    filterCount,
    clearFilters,

    // CRUD
    handleAtualizar,
    handleCreateUser,
    handleExcluir,
    handleSaveEditedUser,
    handleResetPassword,
    handleExportar,

    // Confirm dialog
    confirm,
    ConfirmDialog,
  };
}
